import { type JSX, type ReactElement, type ReactNode, useCallback, useMemo } from 'react';
import { type PopoverState } from 'reakit';
import { useMenuState } from 'reakit/Menu';

import { useAfterFirstRender } from '@cx-modules/hooks';

import { Dropdown } from '../Dropdown';

import styles from './DropdownPill.module.scss';

export interface DropdownPillProps<T> {
  ariaLabel?: string;
  baseId?: string;
  button?: ReactElement | void;
  buttonText?: string;
  items: T[];
  onClick(item: T): void;
  onHide?(): void;
  onShow?(): void;
  placement?: PopoverState['placement'];
  renderItem(item: T, index: number): ReactNode;
  title?: string;
}

export function DropdownPill<T>({
  ariaLabel,
  baseId,
  button,
  buttonText,
  items,
  onClick,
  onHide,
  onShow,
  placement,
  renderItem,
  title,
}: DropdownPillProps<T>): JSX.Element {
  const menu = useMenuState({
    baseId: baseId || undefined,
    gutter: 7,
    placement: placement || 'bottom',
  });
  const { visible } = menu;

  const dropdownLogic = useCallback(() => {
    if (visible && onShow) {
      onShow();
    } else if (!visible && onHide) {
      onHide();
    }
  }, [onHide, onShow, visible]);

  useAfterFirstRender(dropdownLogic);

  const disclosureButton = useMemo(() => {
    if (button) return button;

    return <button className={styles.dropdownPillButton}>{buttonText}</button>;
  }, [button, buttonText]);

  const menuItems = useMemo(() => {
    const itemArr = items.map((item, index) => {
      const isActive = index === 0;
      const props = {
        ...(isActive ? {} : { onClick: () => onClick(item) }),
      };
      return (
        // Key is applied by Reakit in <Dropdown /> component so we can
        // ignore here:
        // eslint-disable-next-line react/jsx-key
        <div
          className={`${styles.dropdownItem}${isActive ? ` ${styles.dropdownItemActive}` : ''}`}
          {...props}
          data-testid="dropdown-item"
        >
          {renderItem(item, index)}
        </div>
      );
    });

    return itemArr;
  }, [items, onClick, renderItem]);

  return (
    <Dropdown
      data-testid="dropdown-menu"
      disclosure={disclosureButton}
      menu={menu}
      menuItems={menuItems}
      menuProps={{
        'aria-label': ariaLabel,
        className: styles.dropdownPanel,
      }}
      title={title}
    />
  );
}
